import type { NarrativeValuation } from '../../hooks/useNarrativeById';
import { isWithinStandardDeviation } from './deviation';
import type {
  FuturePEMultipleCalculationOutput,
  FuturePEMultipleFairValueCalculationOutput,
} from './useFuturePEMultipleCalculation';
import { hasOnlyFairValue } from './validation';

export const calculateValuationInitialisation = ({
  valuation,
  calculateFairValueFromInitialisation,
  calculateRevenueGrowthRateFromFairValue,
}: {
  valuation: NarrativeValuation;
  calculateFairValueFromInitialisation: () => FuturePEMultipleCalculationOutput;
  calculateRevenueGrowthRateFromFairValue: (args: {
    fairValue: number;
    discountRatePercentage: number;
    futurePE: number;
    profitMarginPercentage: number;
    timeFrame: number;
  }) => FuturePEMultipleFairValueCalculationOutput;
}) => {
  const calculatorOutputs = calculateFairValueFromInitialisation();
  // Keep the fair value the user saved on initial load because
  // users might have just wanted that fair value number
  // but for everything else, we want to recalculate on the fly, but
  // in time, the application should self heal
  const fairValue = valuation.fairValue ?? calculatorOutputs.fairValue;
  // Hack to force incorrectly stored fair values to recalibrate or self heal
  // The impact is that the growth rate might not be what the author initially intended, but
  // it is better than changing the fair value number even though it is correct
  const hasStoredIncorrectFairValue = !isWithinStandardDeviation(
    fairValue,
    calculatorOutputs.fairValue
  );

  const revenueGrowthRatePercentage =
    hasStoredIncorrectFairValue || hasOnlyFairValue(valuation)
      ? calculateRevenueGrowthRateFromFairValue({
          fairValue,
          discountRatePercentage: calculatorOutputs.discountRatePercentage,
          futurePE: calculatorOutputs.priceEarningsMultiple,
          profitMarginPercentage: calculatorOutputs.profitMarginPercentage,
          timeFrame: calculatorOutputs.timePeriod,
        }).revenueGrowthRatePercentage
      : calculatorOutputs.revenuePercentage;

  return {
    ...calculatorOutputs,
    fairValue,
    revenuePercentage: revenueGrowthRatePercentage,
  };
};
